import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import get from 'lodash/get'
import keyBy from 'lodash/keyBy'

import { Loading } from '@fielded/shared-ui'
import { PLANNING_TYPES } from '@fielded/fs-api/lib/shipment/tools'

import { makeDeliveryItems, makeDeliveryItemsV2 } from '../../common/makeDeliveryItems'
import { getLastSnapshotSignature, getLastSnapshotSignedBy, batchIdToProductId } from '../../common/utils'
import { isReturnShipment } from '../../../../common/utils/shipment'
import { hasFeature } from '../../../../van-shared/utils/features'

import { withApi } from '../../../../common/ApiProvider'

import { selectMasterData } from '../../../../root/reducers/shipments/master-data'
import SLDeliveryConfirmation from './SLDeliveryConfirmation'

const SLDeliveryConfirmationContainer = ({
  api,
  config,
  shipment,
  formRef,
  isOnline,
  showTotalAmountConfirmation,
  onProceedClick,
  onCloseModal,
  onOpenModal,
  onCommentChange,
  comment
}) => {
  const masterData = useSelector(selectMasterData)
  const productsById = get(masterData, 'products.byId')
  const useShipmentPrices = hasFeature(config, 'features.useShipmentPrices')

  const [items, setItems] = useState([])
  const [name, setName] = useState('')
  const [signature, setSignature] = useState('')
  const [location, setLocation] = useState()
  const [loading, setLoading] = useState(true)

  const initialize = async () => {
    let locationId = shipment.destination.id
    if (isReturnShipment(null, shipment)) {
      locationId = shipment.origin.id
    }

    const productIds = [...new Set(Object.keys(get(shipment, 'counts', {})).map(batchIdToProductId))]
    const priceReferenceDate = get(shipment, 'orderCreatedDate') || get(shipment, 'snapshotDates.new')
    let prices = []
    if (productIds.length > 0 && isOnline) {
      // fetch from agave — guarded with isOnline + timeout race so a hanging
      // request on a degraded or absent connection doesn't leave the user
      // stuck on the loading screen (regression from PR #22869, see #22137)
      const priceFetch = api.price.list({ skus: productIds, referenceDate: priceReferenceDate })
      let timeoutId
      const priceTimeout = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => reject(new Error('price fetch timed out')), 8000)
      })
      try {
        const result = await Promise.race([priceFetch, priceTimeout])
        prices = (result && result.prices) || []
      } catch (priceError) {
        console.warn('Could not fetch prices from Agave, proceeding without them:', priceError)
      } finally {
        clearTimeout(timeoutId)
      }
    }
    const productPricesById = keyBy(prices, 'sku')
    const [location] = await api.location.getLocationsViaIds([locationId])

    const {service: serviceId} = api.service.tools.getServiceForLocationId(locationId)
    const service = await api.service.get(serviceId)

    const ledgerParams = {
      locationId,
      service,
      calculateFields: true
    }

    if (location.level === 'pack-point') {
      ledgerParams.api = api
      ledgerParams.usePGLedger = true
    }

    const report = await api.stock.getLedgerBalanceAsReport(ledgerParams)

    const makeItems = useShipmentPrices ? makeDeliveryItemsV2 : makeDeliveryItems
    const items = makeItems({ shipment, report, productsById, productPricesById, config }) || []

    const signature = getLastSnapshotSignature(shipment)
    const name = getLastSnapshotSignedBy(shipment)

    setItems(items)
    setSignature(signature)
    setName(name)
    setLocation(location)
    setLoading(false)
  }

  useEffect(() => {
    initialize()
  }, [])

  if (loading) {
    return <Loading loadingText='Loading delivery note …' />
  }

  const shipmentCountry = shipment.destination.country

  return (
    <SLDeliveryConfirmation
      location={location}
      shipment={shipment}
      productsById={productsById}
      items={items}
      shipmentCountry={shipmentCountry}
      signature={signature}
      config={config}
      formRef={formRef}
      onProceed={onProceedClick}
      onCloseModal={onCloseModal}
      onOpenModal={onOpenModal}
      comment={comment}
      name={name}
      onCommentChange={onCommentChange}
      showTotalAmountConfirmation={showTotalAmountConfirmation}
      isCollectRedistribute={shipment.planningType === PLANNING_TYPES.C_R}
    />
  )
}

export default withApi(SLDeliveryConfirmationContainer)
